/**
 * Switch Role preview id stored by AssumedRoleContext.
 * Kept out of the API client so fetch helpers and RBAC checks can share it
 * without an import cycle.
 */

const STORAGE_KEY_PREFIX = 'tagp-assumed-role'

function storageKey(): string {
  try {
    const tenantId = localStorage.getItem('tagp-tenant-id')
    return tenantId ? `${STORAGE_KEY_PREFIX}-${tenantId}` : STORAGE_KEY_PREFIX
  } catch {
    return STORAGE_KEY_PREFIX
  }
}

function readStoredRole(): { id?: string; name?: string } | null {
  try {
    const stored = localStorage.getItem(storageKey())
    if (!stored) return null
    return JSON.parse(stored) as { id?: string; name?: string }
  } catch {
    return null
  }
}

/** Role id sent as X-Assume-Role-Id. Null when the admin is not previewing. */
export function getAssumedRoleIdForRequest(): string | null {
  const parsed = readStoredRole()
  return parsed?.id || null
}

/** Assumed role name, or null when nobody is previewing. */
export function readAssumedRoleName(): string | null {
  const parsed = readStoredRole()
  if (!parsed) return null
  return typeof parsed.name === 'string' ? parsed.name : null
}

/** Attach the preview header when a role is assumed. Leaves other headers in place. */
export function mergeAssumedRoleHeader(init?: RequestInit): RequestInit | undefined {
  const roleId = getAssumedRoleIdForRequest()
  if (!roleId) return init
  const headers = new Headers(init?.headers)
  if (!headers.has('X-Assume-Role-Id')) {
    headers.set('X-Assume-Role-Id', roleId)
  }
  return { ...init, headers }
}
